import Box from '@mui/material/Box';
import Divider from '@mui/material/Divider';
import MenuList from '@mui/material/MenuList';
import MenuItem from '@mui/material/MenuItem';
import Checkbox from '@mui/material/Checkbox';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import InputAdornment from '@mui/material/InputAdornment';

import { Iconify } from 'src/components/iconify';
import { CustomPopover } from 'src/components/custom-popover';

// ----------------------------------------------------------------------

type ColumnMenuProps = {
  open: boolean;
  anchorEl: HTMLElement | null;
  onClose: () => void;
  columnSearch: string;
  setColumnSearch: (value: string) => void;
  filteredColumns: any[];
  visibleColumns: string[];
  lockedColumns: string[];
  allVisible: boolean;
  handleToggleColumn: (colId: string) => void;
  handleToggleAll: () => void;
  handleResetColumns: () => void;
  t: (key: string) => string;
};

// ----------------------------------------------------------------------

export function ColumnMenu({
  open,
  anchorEl,
  onClose,
  columnSearch,
  setColumnSearch,
  filteredColumns,
  visibleColumns,
  lockedColumns,
  allVisible,
  handleToggleColumn,
  handleToggleAll,
  handleResetColumns,
  t,
}: ColumnMenuProps) {
  const handleOnClose = () => {
    onClose();
    setColumnSearch('');
  };
  return (
    <CustomPopover
      open={open}
      anchorEl={anchorEl}
      onClose={handleOnClose}
      sx={{ minWidth: 180, p: 1 }}
    >
      <Box sx={{ p: 1.5, pb: 0 }}>
        <TextField
          size="small"
          placeholder="Search"
          value={columnSearch}
          onChange={(e) => setColumnSearch(e.target.value)}
          fullWidth
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Iconify icon="eva:search-fill" sx={{ color: 'text.disabled' }} />
              </InputAdornment>
            ),
          }}
        />
      </Box>

      <MenuList dense sx={{ maxHeight: 250, overflowY: 'auto', mt: 1 }}>
        {filteredColumns?.length === 0 ? (
          <MenuItem
            disabled
            sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}
          >
            <Typography variant="body2">{t('NO_COLUMNS_FOUND')}</Typography>
          </MenuItem>
        ) : (
          filteredColumns.map((col: any) => (
            <MenuItem
              key={col.id}
              onClick={() => handleToggleColumn(col.id)}
              disabled={col?.isDisable}
            >
              <Checkbox checked={visibleColumns.includes(col.id)} />
              <Typography variant="body2">{col.label}</Typography>
            </MenuItem>
          ))
        )}
      </MenuList>

      <Divider sx={{ my: 1 }} />

      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          px: 0.5,
          pb: 1,
        }}
      >
        <Box
          onClick={handleToggleAll}
          sx={{
            px: 0,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            cursor: 'pointer',
          }}
        >
          <Checkbox checked={allVisible} />
          <Typography variant="body2" sx={{ fontSize: 14 }}>
            {t('SHOW_HIDE_ALL')}
          </Typography>
        </Box>

        <Typography
          variant="body2"
          color="primary"
          sx={{ cursor: 'pointer', ml: 1 }}
          onClick={handleResetColumns}
        >
          {t('RESET')}
        </Typography>
      </Box>
    </CustomPopover>
  );
}
